import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Paperclip, Bot, User, RotateCcw, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import {
  invoiceAiChat,
  invoiceAiGetSession,
  invoiceAiUploadDocument,
  invoiceAiSendEmail,
  invoiceAiResetDraft,
  invoiceGenDownloadRecord,
  invoiceGenGetRecord,
} from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import InvoiceAiDraftPanel from "./InvoiceAiDraftPanel";
import InvoiceAiHistoryDialog from "./InvoiceAiHistoryDialog";
import InvoicePreview from "../invoice-gen/InvoicePreview";
import { computeTotals } from "../invoice-gen/totals";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.html,.htm,.txt,.docx";
const STORAGE_KEY = "ashlan_invoice_ai_session_id";

const WELCOME = {
  role: "assistant",
  content:
    "Hi — tell me who to bill and what for (e.g. \"Invoice PartnerRe $450 for a chair massage session on Saturday\"), " +
    "or upload a statement/receipt once we've started talking and I'll pull details from it.",
};

function buildReadyToSend(draft, recipient, recordId) {
  const items = draft.items || [];
  const totals = computeTotals(items, draft.payments || []);
  const serviceSummary =
    items.map((i) => i.service_description || i.notes || i.service_code).filter(Boolean).join(", ") || "Service";
  const serviceDate = (items[0] && items[0].service_date) || draft.invoice_date;
  return {
    invoice_number: draft.invoice_number,
    client_name: draft.bill_to_name,
    service_summary: serviceSummary,
    service_date: serviceDate,
    total: totals.total,
    recipient_email: recipient,
    attachment_filename: `ASHLAN_Invoice_${draft.invoice_number}.pdf`,
    record_id: recordId,
  };
}

function previewDraft(draft) {
  return {
    ...draft,
    items: (draft.items || []).map((it) => ({ ...it, notes: it.notes || it.service_description || it.details || "" })),
  };
}

export default function InvoiceAiChat({ seedMessage, onSeedConsumed }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [draft, setDraft] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState("drafting");
  const [readyToSend, setReadyToSend] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [restoring, setRestoring] = useState(!!localStorage.getItem(STORAGE_KEY));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [resettingDraft, setResettingDraft] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const lastSeedRef = useRef(null);

  const applySession = (session) => {
    setSessionId(session.session_id);
    setMessages(session.messages?.length ? session.messages.map((m) => ({ role: m.role, content: m.content })) : [WELCOME]);
    setDraft(session.draft);
    setWorkflowStatus(session.workflow_status || "drafting");
    setDocs((session.source_documents || []).map((d) => ({ filename: d.filename, doc_id: d.doc_id, kind: d.kind })));
    setRecordId(session.generated_record_id || null);
    if (session.pending_email && session.generated_record_id && session.workflow_status !== "sent") {
      setReadyToSend(buildReadyToSend(session.draft, session.pending_email.recipient, session.generated_record_id));
    } else {
      setReadyToSend(null);
    }
  };

  useEffect(() => {
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (!savedId) return;
    invoiceAiGetSession(savedId)
      .then((session) => {
        applySession(session);
        toast.success("Restored your in-progress invoice draft.");
      })
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        toast.info("Your previous invoice draft had expired — starting a new conversation.");
      })
      .finally(() => setRestoring(false));
  }, []);

  useEffect(() => {
    if (sessionId) localStorage.setItem(STORAGE_KEY, sessionId);
  }, [sessionId]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const autoGrowInput = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    autoGrowInput(e.target);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    await sendMessage(text);
  };

  const sendMessage = async (text) => {
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    scrollToBottom();
    try {
      const res = await invoiceAiChat(sessionId, text);
      setSessionId(res.session_id);
      setDraft(res.draft);
      setWorkflowStatus(res.workflow_status);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      if (res.ready_to_send) {
        setReadyToSend(res.ready_to_send);
        setRecordId(res.ready_to_send.record_id);
      } else if (res.workflow_status === "generated" && !recordId) {
        try {
          const full = await invoiceAiGetSession(res.session_id);
          if (full.generated_record_id) setRecordId(full.generated_record_id);
        } catch {
          /* download button just stays hidden if this lookup fails */
        }
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit an error. Please try again." }]);
      toast.error(err?.response?.data?.detail || "The assistant hit an error.");
    } finally {
      setSending(false);
      scrollToBottom();
    }
  };

  useEffect(() => {
    if (!seedMessage || restoring || sending || lastSeedRef.current === seedMessage) return;
    lastSeedRef.current = seedMessage;
    sendMessage(seedMessage);
    onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedMessage, restoring]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!sessionId) {
      toast.error("Send a message first to start a session, then you can upload a document.");
      return;
    }
    setUploading(true);
    try {
      const res = await invoiceAiUploadDocument(sessionId, file);
      setDocs((d) => [...d, res]);
      setMessages((m) => [
        ...m,
        { role: "system", content: `Uploaded "${res.filename}" — ${res.preview || "processed."}` },
      ]);
      scrollToBottom();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process that file.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!recordId) return;
    setDownloading(true);
    try {
      const blob = await invoiceGenDownloadRecord(recordId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeNo = (draft?.invoice_number || "invoice").replace(/[^A-Za-z0-9-]/g, "");
      a.href = url;
      a.download = `ASHLAN_Invoice_${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!sessionId) return;
    setEmailSending(true);
    try {
      const res = await invoiceAiSendEmail(sessionId);
      toast.success(`Invoice ${res.invoice_number} emailed to ${res.recipient}.`);
      setWorkflowStatus("sent");
      setSentInfo({ recipient: res.recipient, sentAt: new Date().toISOString() });
      setReadyToSend(null);
      setMessages((m) => [...m, { role: "system", content: `Invoice ${res.invoice_number} sent to ${res.recipient}.` }]);
    } catch (err) {
      // The request itself may have failed at the network/edge level (e.g. a
      // Cloudflare timeout) AFTER the backend already sent the email — never
      // assume failure. Re-check the record's true status before telling the
      // admin anything, so they never accidentally send the same invoice twice.
      const backendDetail = err?.response?.data?.detail;
      if (recordId) {
        try {
          const { record } = await invoiceGenGetRecord(recordId);
          if (record.workflow_status === "sent") {
            toast.success(`Invoice ${record.invoice_number} was already sent to ${record.email_recipient} — no need to resend.`);
            setWorkflowStatus("sent");
            setSentInfo({ recipient: record.email_recipient, sentAt: record.email_sent_at });
            setReadyToSend(null);
            setEmailSending(false);
            return;
          }
        } catch {
          // status check itself failed — fall through to the generic error below
        }
      }
      toast.error(backendDetail || "Could not send the email — confirmed NOT sent, safe to try again.");
    } finally {
      setEmailSending(false);
    }
  };

  const handleNewConversation = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSessionId(null);
    setMessages([WELCOME]);
    setInput("");
    setDraft(null);
    setWorkflowStatus("drafting");
    setReadyToSend(null);
    setSentInfo(null);
    setRecordId(null);
    setDocs([]);
  };

  const handleSelectHistory = async (id) => {
    setHistoryOpen(false);
    try {
      const session = await invoiceAiGetSession(id);
      applySession(session);
      scrollToBottom();
    } catch {
      toast.error("Could not open that conversation.");
    }
  };

  const handleStartNewInvoice = async () => {
    if (!sessionId) return;
    setResettingDraft(true);
    try {
      const res = await invoiceAiResetDraft(sessionId);
      setDraft(res.draft);
      setWorkflowStatus(res.workflow_status);
      setReadyToSend(null);
      setSentInfo(null);
      setRecordId(null);
      setMessages((m) => [...m, { role: "system", content: "— Started a new invoice draft —" }]);
      toast.success("Draft cleared — ready for a new invoice.");
      scrollToBottom();
    } catch {
      toast.error("Could not start a new invoice draft.");
    } finally {
      setResettingDraft(false);
    }
  };

  const canPreview = draft && (draft.bill_to_name || (draft.items || []).length > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-testid="invoice-ai-panel">
      <div className="border border-[#E5E7EB] rounded-lg flex flex-col h-[calc(100vh-220px)] min-h-[560px]">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#F3F4F6]">
          <p className="text-xs text-[#9CA3AF]">
            {sessionId ? "Draft auto-saves as you go — safe to refresh." : "New conversation"}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              data-testid="invoice-ai-history-button"
              className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#080808]"
            >
              <HistoryIcon size={12} /> History
            </button>
            <button
              type="button"
              onClick={handleNewConversation}
              data-testid="invoice-ai-new-conversation-button"
              className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#080808]"
            >
              <RotateCcw size={12} /> New Conversation
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3" data-testid="invoice-ai-messages">
          {restoring && (
            <div className="self-center text-xs text-[#9CA3AF] flex items-center gap-1.5" data-testid="invoice-ai-restoring">
              <Loader2 size={12} className="animate-spin" /> Restoring your draft…
            </div>
          )}
          {messages.map((m, idx) => (
            <div
              key={idx}
              data-testid={`invoice-ai-message-${idx}`}
              className={
                m.role === "user"
                  ? "self-end max-w-[80%] bg-[#080808] text-white rounded-lg rounded-br-sm px-3.5 py-2.5 text-sm flex items-start gap-2"
                  : m.role === "system"
                  ? "self-center text-xs text-[#9CA3AF] italic px-2"
                  : "self-start max-w-[80%] bg-[#F9FAFB] border border-[#E5E7EB] text-[#080808] rounded-lg rounded-bl-sm px-3.5 py-2.5 text-sm flex items-start gap-2"
              }
            >
              {m.role === "assistant" && <Bot size={14} className="text-[#3A7A8C] shrink-0 mt-0.5" />}
              {m.role === "user" && <User size={14} className="shrink-0 mt-0.5 opacity-70" />}
              {m.role !== "system" ? <span className="whitespace-pre-wrap">{m.content}</span> : m.content}
            </div>
          ))}
          {sending && (
            <div className="self-start text-xs text-[#9CA3AF] flex items-center gap-1.5 px-1" data-testid="invoice-ai-typing">
              <Loader2 size={12} className="animate-spin" /> Thinking…
            </div>
          )}
        </div>

        {docs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-2" data-testid="invoice-ai-doc-chips">
            {docs.map((d, i) => (
              <span key={i} className="text-[10px] bg-[#F3F4F6] text-[#3C4048] rounded-full px-2 py-0.5">
                {d.filename}
              </span>
            ))}
          </div>
        )}

        <div className="border-t border-[#E5E7EB] p-3 flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            onChange={handleFileChange}
            className="hidden"
            data-testid="invoice-ai-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="invoice-ai-upload-button"
            title="Upload a document (PDF, JPG, PNG, HTML, TXT, DOCX)"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#D1D5DB] text-[#6B7280] hover:border-[#3A7A8C] hover:text-[#3A7A8C] disabled:opacity-60"
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
          </button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message…"
            rows={1}
            data-testid="invoice-ai-input"
            className="ash-input flex-1 resize-none min-h-[38px] max-h-[200px] overflow-y-auto"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            data-testid="invoice-ai-send-message-button"
            className="ash-btn-primary shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md disabled:opacity-60"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>

      <InvoiceAiDraftPanel
        draft={draft}
        workflowStatus={workflowStatus}
        readyToSend={readyToSend}
        sentInfo={sentInfo}
        onDownload={handleDownload}
        onSend={handleSendEmail}
        onPreview={canPreview ? () => setPreviewOpen(true) : null}
        onStartNewInvoice={sessionId && draft ? handleStartNewInvoice : null}
        resettingDraft={resettingDraft}
        downloading={downloading}
        sending={emailSending}
      />

      <InvoiceAiHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        currentSessionId={sessionId}
        onSelect={handleSelectHistory}
        onDeletedCurrent={handleNewConversation}
      />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] overflow-y-auto"
          data-testid="invoice-ai-preview-dialog"
        >
          <DialogTitle className="sr-only">Invoice Preview</DialogTitle>
          <DialogDescription className="sr-only">A visual preview of the current invoice draft.</DialogDescription>
          {draft && <InvoicePreview draft={previewDraft(draft)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
